import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { toast } from 'react-toastify';

import FirebaseImage from '../../Components/Media/FirebaseImage';
import Loader from '../../Components/UI/Loader/Loader';
import { getDemandsForArtisanAPI } from '../../Store/Actions/demandActions';
import { savePortfolioProject, setPortfolioProjectStatus } from '../../Store/Actions/actionOffer';
import {
  getPortfolioBeforeAfter,
  getPortfolioMedia,
  getPortfolioStatus,
  getPortfolioSummary,
  getPortfolioTitle,
  PORTFOLIO_MEDIA_SOURCE,
  PORTFOLIO_STATUS,
  sortPortfolioProjects,
} from '../../Utils/portfolio';
import PortfolioProjectEditor from './PortfolioProjectEditor';
import JobModal from './JobModal';
import { subscribePublishedReviews } from '../../Services/trustReviews';
import { createOfficialInvoiceDraft, issueOfficialInvoice } from '../../Services/invoicing';
import ProjectInvoiceModal from '../../Components/Invoicing/ProjectInvoiceModal';
import { useI18n } from '../../i18n/I18nProvider';

const MyBoutique = ({
  artisanId,
  profile,
  loading,
  userDemands = [],
  getDemands,
  savePortfolio,
  changePortfolioStatus,
}) => {
  const history = useHistory();
  const { pick, formatDate } = useI18n();
  const [activeFilter, setActiveFilter] = useState('published');
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeModal, setActiveModal] = useState('');
  const [reviewsByDemand, setReviewsByDemand] = useState({});
  const [invoiceBusyId, setInvoiceBusyId] = useState('');
  const [invoiceViewerId, setInvoiceViewerId] = useState('');
  const filters = useMemo(() => [
    { id: 'published', label: pick('Publiés', 'Published') },
    { id: 'draft', label: pick('Brouillons', 'Drafts') },
    { id: 'available', label: pick('À ajouter', 'Ready to add') },
    { id: 'hidden', label: pick('Masqués', 'Hidden') },
  ], [pick]);

  useEffect(() => {
    const unsubscribe = getDemands();
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [getDemands]);

  useEffect(() => {
    if (!artisanId) return undefined;
    return subscribePublishedReviews(
      { revieweeId: artisanId },
      (reviews) => setReviewsByDemand(Object.fromEntries(reviews.map((review) => [review.demand_id, review]))),
      () => setReviewsByDemand({}),
    );
  }, [artisanId]);

  const completedProjects = useMemo(() => userDemands.filter((demand) => (
    demand?.demand_status === 'reviewed'
      && demand?.contract?.artisan_id === artisanId
  )), [userDemands, artisanId]);

  const groups = useMemo(() => completedProjects.reduce((result, project) => {
    const status = getPortfolioStatus(project);
    result[status] = [...(result[status] || []), project];
    return result;
  }, { published: [], draft: [], available: [], hidden: [] }), [completedProjects]);

  const projects = useMemo(
    () => sortPortfolioProjects(groups[activeFilter] || []),
    [groups, activeFilter],
  );

  const profileScore = useMemo(() => {
    const checks = [
      Boolean(profile?.profile_image),
      Boolean(profile?.speciality),
      Boolean(profile?.artisan_region),
      Boolean(profile?.phone_number),
      Boolean(profile?.business_intro),
      Array.isArray(profile?.certifications) && profile.certifications.length > 0,
      (groups.published?.length || 0) >= 1,
      (groups.published?.length || 0) >= 3,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [profile, groups.published]);

  const openEditor = (project) => {
    setSelectedProject(project);
    setActiveModal('editor');
  };
  const openPreview = (project) => {
    setSelectedProject(project);
    setActiveModal('preview');
  };
  const closeModal = () => {
    setSelectedProject(null);
    setActiveModal('');
  };

  const save = (values) => savePortfolio({
    demandId: selectedProject?.demand_id,
    artisanId,
    ...values,
  });

  const setStatus = async (project, status) => {
    const success = await changePortfolioStatus({ demandId: project.demand_id, artisanId, status });
    if (success && status === 'published') setActiveFilter('published');
  };

  const issueProjectInvoice = async (project) => {
    if (!project?.demand_id || invoiceBusyId) return;
    const platform = profile?.invoicing_profile?.approved_platform || {};
    if (!platform.refresh_token_encrypted) {
      toast.info(pick('Connectez d’abord votre plateforme de facturation depuis Mon profil.', 'Connect your invoicing platform from My profile first.'));
      history.push('/invoicing');
      return;
    }
    if (platform.session_status !== 'verified') {
      toast.info(pick('Votre entreprise est encore en cours de vérification. Actualisez le statut depuis Mon profil avant d’émettre la facture.', 'Your business is still being verified. Refresh the status from My profile before issuing an invoice.'));
      history.push('/invoicing');
      return;
    }
    const existingStatus = project?.invoicing?.status || '';
    if (['submitted', 'delivered', 'accepted', 'paid'].includes(existingStatus)) {
      setInvoiceViewerId(project.demand_id);
      return;
    }
    if (!window.confirm(pick('Émettre et transmettre la facture finale ? Après numérotation, elle ne pourra plus être modifiée.', 'Issue and transmit the final invoice? Once numbered, it can no longer be edited.'))) return;
    setInvoiceBusyId(project.demand_id);
    try {
      const draft = await createOfficialInvoiceDraft(project.demand_id);
      await issueOfficialInvoice(project.demand_id);
      toast.success(pick('Facture transmise à la plateforme agréée.', 'Invoice transmitted to the approved platform.'));
      await getDemands();
      setInvoiceViewerId(project.demand_id);
    } catch (error) {
      toast.error(error?.message || pick('La facture n’a pas pu être transmise.', 'The invoice could not be transmitted.'));
    } finally {
      setInvoiceBusyId('');
    }
  };


  return (
    <Section>
      <DashboardHeader>
        <div>
          <h2>{pick('Mes réalisations', 'My portfolio projects')}</h2>
        </div>
        <HeaderActions>
          <SecondaryButton type="button" onClick={() => history.push('/artisanboutique', { artisanId })}>
            <PreviewOutlinedIcon /> {pick('Prévisualiser ma vitrine', 'Preview my portfolio')}
          </SecondaryButton>
        </HeaderActions>
      </DashboardHeader>

      <Overview>
        <CompletionCard>
          <CompletionTop>
            <div><AutoAwesomeOutlinedIcon /><strong>{pick(`Votre vitrine est complète à ${profileScore}%`, `Your portfolio is ${profileScore}% complete`)}</strong></div>
            <span>{profileScore}%</span>
          </CompletionTop>
          <CompletionTrack><CompletionValue $value={profileScore} /></CompletionTrack>
          <CompletionTips>
            {!profile?.profile_image && <span>{pick('Ajoutez une photo de profil.', 'Add a profile photo.')}</span>}
            {!profile?.speciality && <span>{pick('Renseignez votre spécialité.', 'Add your speciality.')}</span>}
            {!profile?.business_intro && <span>{pick('Ajoutez une présentation professionnelle dans « Mon profil ».', 'Add a professional introduction in My profile.')}</span>}
            {(!Array.isArray(profile?.certifications) || profile.certifications.length === 0) && <span>{pick('Ajoutez vos certifications vérifiables si vous en avez.', 'Add verifiable certifications if you have any.')}</span>}
            {(groups.published?.length || 0) === 0 && <span>{pick('Publiez une première réalisation.', 'Publish your first project.')}</span>}
            {(groups.published?.length || 0) > 0 && (groups.published?.length || 0) < 3 && <span>{pick('Présentez 3 projets pour inspirer davantage confiance.', 'Show 3 projects to build more trust.')}</span>}
            {profileScore === 100 && <span>{pick('Votre vitrine est prête à convaincre de nouveaux clients.', 'Your portfolio is ready to win new clients.')}</span>}
          </CompletionTips>
        </CompletionCard>

        <StatCard><StorefrontOutlinedIcon /><div><strong>{groups.published?.length || 0}</strong><span>{pick('projets publiés', 'published projects')}</span></div></StatCard>
        <StatCard><AddPhotoAlternateOutlinedIcon /><div><strong>{groups.available?.length || 0}</strong><span>{pick('projets disponibles', 'projects ready to add')}</span></div></StatCard>
        <StatCard><InsightsOutlinedIcon /><div><strong>—</strong><span>{pick('vues bientôt disponibles', 'views coming soon')}</span></div></StatCard>
      </Overview>

      <Tabs role="tablist" aria-label={pick('État des projets de vitrine', 'Portfolio project status')}>
        {filters.map((filter) => (
          <Tab
            key={filter.id}
            type="button"
            role="tab"
            aria-selected={activeFilter === filter.id}
            $active={activeFilter === filter.id}
            onClick={() => setActiveFilter(filter.id)}
          >
            {filter.label}<strong>{groups[filter.id]?.length || 0}</strong>
          </Tab>
        ))}
      </Tabs>

      {loading && completedProjects.length === 0 ? (
        <LoaderWrapper><Loader /></LoaderWrapper>
      ) : projects.length === 0 ? (
        <EmptyState>
          <StorefrontOutlinedIcon />
          <h3>{activeFilter === 'published' ? pick('Votre vitrine attend sa première réalisation', 'Your portfolio is waiting for its first project') : pick('Aucun projet dans cette catégorie', 'No project in this category')}</h3>
          <p>
            {activeFilter === 'published'
              ? pick('Ouvrez « À ajouter », choisissez un projet terminé, sélectionnez les meilleures photos puis publiez-le.', 'Open “Ready to add”, choose a completed project, select the best photos, then publish it.')
              : pick('Les projets apparaîtront ici selon leur état de publication.', 'Projects will appear here according to their publication status.')}
          </p>
          {activeFilter === 'published' && groups.available?.length > 0 && (
            <PrimaryButton type="button" onClick={() => setActiveFilter('available')}>{pick('Choisir un projet', 'Choose a project')}</PrimaryButton>
          )}
        </EmptyState>
      ) : (
        <Gallery>
          {projects.map((project) => {
            const portfolio = project.portfolio || {};
            const status = getPortfolioStatus(project);
            const media = getPortfolioMedia(project);
            const clientPhotoCount = media.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.CLIENT).length;
            const artisanPhotoCount = media.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.ARTISAN).length;
            const beforeAfter = getPortfolioBeforeAfter(project);
            const title = getPortfolioTitle(project);
            const summary = getPortfolioSummary(project);
            const rating = Number(reviewsByDemand[project.demand_id]?.rating || 0);
            const invoiceStatus = project.invoicing?.status || '';
            const invoiceReady = project.demand_status === 'reviewed'
              && (project.payments?.final?.status === 'paid' || Boolean(project.paid_at));
            const invoiceTransmitted = ['submitted', 'delivered', 'accepted', 'paid'].includes(invoiceStatus);
            const platformVerified = profile?.invoicing_profile?.approved_platform?.session_status === 'verified';

            return (
              <ProjectCard
                key={project.demand_id}
                $featured={portfolio.featured}
                $interactive
                tabIndex={0}
                aria-label={status === 'available'
                  ? pick(`Ajouter ${title} à ma vitrine`, `Add ${title} to my portfolio`)
                  : pick(`Ouvrir l’aperçu de ${title}`, `Open preview of ${title}`)}
                onClick={() => (status === 'available' ? openEditor(project) : openPreview(project))}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  status === 'available' ? openEditor(project) : openPreview(project);
                }}
              >
                <Visual>
                  <ProjectImage src={media[0]?.url} fallbackSrc="/images/project-placeholder.svg" alt={title} />
                  <VisualTop>
                    {portfolio.featured && <FeaturedBadge>★ {pick('Projet à la une', 'Featured project')}</FeaturedBadge>}
                    {rating > 0 && <RatingBadge>★ {rating}/5</RatingBadge>}
                  </VisualTop>
                  {beforeAfter && <CompareBadge>↔ {pick('Avant / Après', 'Before / After')}</CompareBadge>}
                </Visual>
                <ProjectBody>
                  <ProjectMeta>
                    <span>{formatDate(project.date)}</span>
                    <StatusBadge $status={status}>
                      {status === 'published' ? pick('Publié', 'Published') : status === 'draft' ? pick('Brouillon', 'Draft') : status === 'hidden' ? pick('Masqué', 'Hidden') : pick('Disponible', 'Ready to add')}
                    </StatusBadge>
                  </ProjectMeta>
                  <h3 className="user-content">{title}</h3>
                  <p className="user-content">{summary || pick('Ajoutez un titre et une description pour valoriser ce chantier.', 'Add a title and description to showcase this project.')}</p>
                  <MediaSummary>
                    {clientPhotoCount > 0 && <span>{pick('Photos client', 'Client photos')} · {clientPhotoCount}</span>}
                    {artisanPhotoCount > 0 && <span>{pick('Photos artisan', 'Artisan photos')} · {artisanPhotoCount}</span>}
                  </MediaSummary>

                  <Actions>
                    <PrimaryButton type="button" onClick={(event) => { event.stopPropagation(); openEditor(project); }}>
                      {status === 'available' ? <AddPhotoAlternateOutlinedIcon /> : <EditOutlinedIcon />}
                      {status === 'available' ? pick('Ajouter à la vitrine', 'Add to portfolio') : pick('Modifier', 'Edit')}
                    </PrimaryButton>
                    {status === 'draft' && (
                      <SecondaryButton type="button" onClick={(event) => { event.stopPropagation(); setStatus(project, PORTFOLIO_STATUS.PUBLISHED); }}>
                        <PublishOutlinedIcon /> {pick('Publier', 'Publish')}
                      </SecondaryButton>
                    )}
                    {status === 'published' && (
                      <SecondaryButton type="button" onClick={(event) => { event.stopPropagation(); setStatus(project, PORTFOLIO_STATUS.HIDDEN); }}>
                        <VisibilityOffOutlinedIcon /> {pick('Masquer', 'Hide')}
                      </SecondaryButton>
                    )}
                    {invoiceReady && (
                      <InvoiceButton
                        type="button"
                        $ready={invoiceTransmitted}
                        disabled={invoiceBusyId === project.demand_id}
                        onClick={(event) => {
                          event.stopPropagation();
                          invoiceTransmitted
                            ? setInvoiceViewerId(project.demand_id)
                            : issueProjectInvoice(project);
                        }}
                        title={!platformVerified && !invoiceTransmitted
                          ? pick('La plateforme agréée doit être vérifiée avant émission.', 'The approved platform must be verified before issuing the invoice.')
                          : ''}
                      >
                        <ReceiptLongOutlinedIcon />
                        {invoiceBusyId === project.demand_id
                          ? pick('Transmission…', 'Transmitting…')
                          : invoiceTransmitted
                            ? `${pick('Voir', 'View')} ${project.invoicing?.invoice_number || pick('la facture', 'invoice')}`
                            : invoiceStatus === 'transmission_failed'
                              ? pick('Réessayer la facture', 'Retry invoice')
                              : pick('Émettre la facture finale', 'Issue final invoice')}
                      </InvoiceButton>
                    )}
                  </Actions>
                </ProjectBody>
              </ProjectCard>
            );
          })}
        </Gallery>
      )}

      <PortfolioProjectEditor
        opened={activeModal === 'editor'}
        project={selectedProject}
        saving={loading}
        onClose={closeModal}
        onSave={save}
      />
      <ProjectInvoiceModal
        demandId={invoiceViewerId}
        open={Boolean(invoiceViewerId)}
        onClose={() => setInvoiceViewerId('')}
      />
      <JobModal
        checkartisan="false"
        showModal={activeModal === 'preview' ? 'open' : 'close'}
        handleClick={closeModal}
        demand={selectedProject}
        allowCoverUpload={false}
      />
    </Section>
  );
};

const Section = styled.section`width: 100%; min-width: 0;`;
const DashboardHeader = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; padding: clamp(1.15rem, 3vw, 1.65rem); border: 1px solid #e5e1da; border-radius: 1.2rem; background: #fff; box-shadow: 0 .8rem 2rem rgba(0,0,0,.05);
  h2 { margin: 0; color: #111111; font-size: clamp(1.35rem, 3vw, 1.9rem); }
  p { max-width: 44rem; margin: .45rem 0 0; color: #66615b; line-height: 1.55; }
  @media (max-width: 48rem) { flex-direction: column; }
`;
const Eyebrow = styled.div`margin-bottom: .35rem; color: #a88a5d; font-size: .72rem; font-weight: 900; letter-spacing: .11em; text-transform: uppercase;`;
const HeaderActions = styled.div`display: flex; flex: 0 0 auto; gap: .5rem;`;
const Overview = styled.div`display: grid; grid-template-columns: minmax(16rem,2fr) repeat(3,minmax(8rem,1fr)); gap: .75rem; margin-bottom: 1rem; @media (max-width: 62rem) { grid-template-columns: repeat(3,minmax(0,1fr)); ${'' /* completion spans */} } @media (max-width: 38rem) { grid-template-columns: 1fr; }`;
const CompletionCard = styled.section`padding: 1rem; border: 1px solid #ead8ad; border-radius: 1rem; background: linear-gradient(135deg,#fbf7f1,#fff); @media (max-width: 62rem) { grid-column: 1 / -1; }`;
const CompletionTop = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 1rem; color: #5e4a20; div { display: flex; align-items: center; gap: .45rem; } svg { color: #b17b16; } > span { font-weight: 900; }`;
const CompletionTrack = styled.div`height: .48rem; margin: .75rem 0; overflow: hidden; border-radius: 999px; background: #f1e5c8;`;
const CompletionValue = styled.div`width: ${({ $value }) => `${$value}%`}; height: 100%; border-radius: inherit; background: linear-gradient(90deg,#c8912c,#e2b654);`;
const CompletionTips = styled.div`display: grid; gap: .2rem; color: #75633d; font-size: .78rem;`;
const StatCard = styled.div`display: flex; align-items: center; gap: .7rem; padding: 1rem; border: 1px solid #e5e1da; border-radius: 1rem; background: #fff; color: #111111; svg { flex: 0 0 auto; color: #9c6d16; } div { display: grid; } strong { font-size: 1.35rem; line-height: 1; } span { margin-top: .2rem; color: #6e7d87; font-size: .7rem; text-transform: uppercase; }`;
const Tabs = styled.div`display: flex; gap: .5rem; overflow-x: auto; margin-bottom: 1rem; padding-bottom: .25rem;`;
const Tab = styled.button`
  min-width: max-content; display: inline-flex; align-items: center; gap: .5rem; padding: .65rem .8rem; border: 1px solid ${({ $active }) => ($active ? '#111111' : '#e7dfd2')}; border-radius: 999px; background: ${({ $active }) => ($active ? '#111111' : '#fff')}; color: ${({ $active }) => ($active ? '#fff' : '#5f5a52')}; font: inherit; font-size: .8rem; font-weight: 850; cursor: pointer;
  strong { min-width: 1.3rem; padding: .1rem .3rem; border-radius: 999px; background: ${({ $active }) => ($active ? 'rgba(255,255,255,.18)' : '#f1ece5')}; }
`;
const Gallery = styled.div`display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 1rem; @media (max-width: 50rem) { grid-template-columns: 1fr; }`;
const ProjectCard = styled.article`
  min-width: 0;
  overflow: hidden;
  border: 2px solid ${({ $featured }) => ($featured ? '#d8ad51' : '#e5e1da')};
  border-radius: 1.15rem;
  background: #fff;
  box-shadow: 0 .8rem 2.1rem rgba(0,0,0,.06);
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;

  ${({ $interactive }) => $interactive && `
    &:hover { transform: translateY(-2px); border-color: #c9ad78; box-shadow: 0 1rem 2.4rem rgba(0,0,0,.1); }
    &:focus-visible { outline: 3px solid rgba(192,163,117,.38); outline-offset: 3px; }
  `}
`;
const Visual = styled.div`position: relative; min-height: 13rem; background: #f1ece5;`;
const ProjectImage = styled(FirebaseImage)`width: 100%; height: 15rem; display: block; object-fit: contain; padding: .55rem; box-sizing: border-box; background: #f1ece5;`;
const VisualTop = styled.div`position: absolute; inset: .7rem .7rem auto; display: flex; align-items: center; justify-content: space-between; gap: .5rem; pointer-events: none;`;
const FeaturedBadge = styled.span`padding: .38rem .58rem; border-radius: 999px; background: rgba(255,244,217,.94); color: #715016; font-size: .7rem; font-weight: 900;`;
const RatingBadge = styled.span`margin-left: auto; padding: .38rem .58rem; border-radius: 999px; background: rgba(255,255,255,.94); color: #755418; font-size: .72rem; font-weight: 900;`;
const CompareBadge = styled.span`position: absolute; left: .7rem; bottom: .7rem; padding: .35rem .55rem; border-radius: 999px; background: rgba(0,0,0,.9); color: #fff; font-size: .67rem; font-weight: 900; backdrop-filter: blur(8px);`;
const ProjectBody = styled.div`display: grid; gap: .75rem; padding: 1rem; h3 { margin: 0; color: #18364d; font-size: 1.08rem; } p { margin: 0; overflow: hidden; color: #66615b; line-height: 1.55; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; }`;
const MediaSummary = styled.div`display: flex; flex-wrap: wrap; gap: .35rem; span { padding: .3rem .48rem; border-radius: 999px; background: #f1f5f7; color: #5d707c; font-size: .67rem; font-weight: 800; }`;
const ProjectMeta = styled.div`display: flex; align-items: center; justify-content: space-between; gap: .75rem; color: #726b61; font-size: .72rem;`;
const StatusBadge = styled.span`padding: .3rem .52rem; border-radius: 999px; background: ${({ $status }) => ($status === 'published' ? '#e7f7ee' : $status === 'draft' ? '#fff4d9' : '#f1ece5')}; color: ${({ $status }) => ($status === 'published' ? '#1f6845' : $status === 'draft' ? '#75551a' : '#5b6c77')}; font-weight: 850;`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: .5rem; padding-top: .2rem;`;
const BaseButton = styled.button`min-height: 2.55rem; display: inline-flex; align-items: center; justify-content: center; gap: .4rem; padding: .62rem .8rem; border-radius: .75rem; font: inherit; font-size: .78rem; font-weight: 850; cursor: pointer; svg { font-size: 1rem; }`;
const PrimaryButton = styled(BaseButton)`border: 0; background: #111111; color: #fff;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #d5dfe5; background: #fff; color: #2d4c61;`;
const InvoiceButton = styled(BaseButton)`
  margin-left: auto;
  border: 1px solid ${({ $ready }) => ($ready ? '#addfc2' : '#d2b57e')};
  background: ${({ $ready }) => ($ready ? '#e8f7ef' : '#fff7e8')};
  color: ${({ $ready }) => ($ready ? '#1f6845' : '#694814')};
  &:disabled { opacity: .58; cursor: wait; }
  @media (max-width: 36rem) { margin-left: 0; width: 100%; }
`;
const EmptyState = styled.div`display: grid; justify-items: center; gap: .45rem; padding: 2.6rem 1.2rem; border: 1px dashed #cbd7df; border-radius: 1rem; background: #fff; color: #65747f; text-align: center; svg { color: #9e711e; font-size: 2.5rem; } h3 { margin: .25rem 0 0; color: #233d51; } p { max-width: 38rem; margin: 0 0 .5rem; line-height: 1.6; }`;
const LoaderWrapper = styled.div`min-height: 18rem; display: grid; place-items: center;`;

const mapStateToProps = ({ firebase, demands }) => ({
  artisanId: firebase?.auth?.uid || '',
  profile: firebase?.profile || {},
  loading: Boolean(demands?.loading),
  userDemands: demands?.demands || [],
});
const mapDispatchToProps = (dispatch) => ({
  getDemands: () => dispatch(getDemandsForArtisanAPI()),
  savePortfolio: (payload) => dispatch(savePortfolioProject(payload)),
  changePortfolioStatus: (payload) => dispatch(setPortfolioProjectStatus(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyBoutique);
